import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useToast } from '../../components/ui/Toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import submissionService from '../../services/submissionService';
import reviewerService from '../../services/reviewerService';
import reviewCriteriaService from '../../services/reviewCriteriaService';
import ReviewForm from './components/ReviewForm';
import PDFViewer from './components/PDFViewer';
import ReviewerQueuePanel from './components/ReviewerQueuePanel';

const StreamlinedReviewWorkspace = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('reviews');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams?.get('submissionId');
  const { user } = useAuth();

  const [activePanel, setActivePanel] = useState('review'); // review, queue
  const [submission, setSubmission] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [reviewCriteria, setReviewCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const autoSaveTimerRef = useRef(null);
  const loadedKeyRef = useRef(null); // prevents re-loading when auth refreshes user object reference

  const [reviewForm, setReviewForm] = useState({
    overallScore: 5,
    detailedFeedback: '',
    strengths: '',
    weaknesses: '',
    decision: 'accept',
    confidenceLevel: 3
  });

  const [criteriaAnswers, setCriteriaAnswers] = useState({});

  // Inline validation — replaces the old ReviewCompletionValidator component
  const canSubmit = useMemo(() => {
    const hasScore = reviewForm?.overallScore >= 1 && reviewForm?.overallScore <= 10;
    const requiredCriteria = reviewCriteria?.filter(c => c?.isRequired) || [];
    const allRequiredAnswered = requiredCriteria.every(c => {
      const answer = criteriaAnswers?.[c?.id];
      return answer !== undefined && answer !== null && answer !== '';
    });
    return hasScore && allRequiredAnswered;
  }, [reviewForm?.overallScore, reviewCriteria, criteriaAnswers]);

  // Auto-save functionality (every 30 seconds) — silent, no UI error banners
  useEffect(() => {
    if (!submissionId || !user?.id || !submission?.conferenceId) return;

    const autoSave = async () => {
      try {
        await reviewerService?.saveDraft({
          submissionId,
          reviewerId: user?.id,
          conferenceId: submission?.conferenceId,
          overallScore: reviewForm?.overallScore,
          detailedFeedback: reviewForm?.detailedFeedback,
          strengths: reviewForm?.strengths,
          weaknesses: reviewForm?.weaknesses,
          decision: reviewForm?.decision,
          confidenceLevel: reviewForm?.confidenceLevel,
          criteriaAnswers
        });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 3000);
      } catch (err) {
        // Auto-save fails silently — only manual saves show error banners
        console.warn('Auto-save failed (silent):', err?.message || err);
      }
    };

    autoSaveTimerRef.current = setInterval(autoSave, 30000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [submissionId, user?.id, submission?.conferenceId, reviewForm, criteriaAnswers]);

  useEffect(() => {
    if (!user?.id) {
      navigate('/user-sign-in');
      return;
    }
    const loadKey = `${user.id}:${submissionId ?? ''}`;
    if (loadedKeyRef.current === loadKey) return;
    loadedKeyRef.current = loadKey;
    loadWorkspaceData();
  }, [user?.id, submissionId]);

  const loadWorkspaceData = async () => {
    try {
      setLoading(true);
      setError('');

      const assignedPapers = await reviewerService?.getAssignedPapers(user?.id);
      setAssignments(assignedPapers);

      if (submissionId) {
        // Verify access first (reads from submission_reviews — always works for assigned reviewers)
        const accessCheck = await submissionService?.verifyReviewerAccess(user?.id, submissionId);
        if (!accessCheck?.hasAccess) {
          setError(t('reviewWorkspace.accessDenied', { reason: accessCheck?.reason }));
          return;
        }

        let data;
        try {
          data = await submissionService?.getById(submissionId);
        } catch (err) {
          if (err?.code === 'PGRST116') {
            setError('Could not load submission data. Please try again or contact the conference administrator.');
            return;
          }
          throw err;
        }
        setSubmission(data);

        // Get a secure signed URL for downloading the submission file.
        if (data?.pdfUrl) {
          try {
            const ext = data.pdfUrl.split('.').pop() || 'pdf';
            const num = data?.paperNumber ?? '';
            const shortName = data?.conferenceShortName || '';
            const downloadName = `${num ? num + '-' : ''}${shortName || 'paper'}.${ext}`;
            const urlData = await submissionService?.getSubmissionFileUrl(data?.pdfUrl, 3600, user?.id, downloadName);
            setFileUrl(urlData?.url || '');
          } catch (err) {
            console.error('Failed to get submission file URL:', err);
          }
        }

        if (data?.conferenceId) {
          const criteria = await reviewCriteriaService?.getByConference(data?.conferenceId);
          setReviewCriteria(criteria);

          const existingReview = await reviewerService?.getReviewForSubmission(submissionId, user?.id);
          if (existingReview) {
            setReviewForm({
              overallScore: existingReview?.overallScore || 5,
              detailedFeedback: existingReview?.detailedFeedback || '',
              strengths: existingReview?.strengths || '',
              weaknesses: existingReview?.weaknesses || '',
              decision: existingReview?.decision || 'accept',
              confidenceLevel: existingReview?.confidenceLevel || 3
            });

            const answersData = await reviewerService?.getReviewAnswers(submissionId, user?.id);
            if (answersData?.map) {
              // answersData.map values are answer objects { value, answerNumeric, ... }
              // extract the primitive value so sliders/inputs receive a plain number/string/boolean
              const primitiveAnswers = {};
              Object.entries(answersData.map).forEach(([criteriaId, answerObj]) => {
                primitiveAnswers[criteriaId] = answerObj?.value ?? answerObj;
              });
              setCriteriaAnswers(primitiveAnswers);
            }
          } else {
            const initialAnswers = {};
            criteria?.forEach(c => {
              if (c?.questionType === 'scale') {
                initialAnswers[c?.id] = Math.floor(((c?.scaleMin || 1) + (c?.scaleMax || 5)) / 2);
              } else if (c?.questionType === 'true_false') {
                initialAnswers[c?.id] = null;
              } else {
                initialAnswers[c?.id] = '';
              }
            });
            setCriteriaAnswers(initialAnswers);
          }
        }
      }
    } catch (err) {
      console.error('Load workspace error:', err);
      setError(err?.message || t('reviewWorkspace.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    try {
      if (!canSubmit) {
        setError(t('reviewWorkspace.completeRequiredFields'));
        return;
      }

      setSaving(true);
      setError('');

      await reviewerService?.submitReview({
        submissionId,
        reviewerId: user?.id,
        conferenceId: submission?.conferenceId,
        overallScore: reviewForm?.overallScore,
        detailedFeedback: reviewForm?.detailedFeedback,
        strengths: reviewForm?.strengths,
        weaknesses: reviewForm?.weaknesses,
        decision: reviewForm?.decision,
        confidenceLevel: reviewForm?.confidenceLevel,
        criteriaAnswers
      });

      setError('');
      await loadWorkspaceData();
      setActivePanel('queue');

      const successMsg = t('reviewWorkspace.reviewSubmittedSuccess');
      setTimeout(() => {
        toastSuccess(successMsg);
      }, 300);
    } catch (err) {
      setError(err?.message || 'Failed to submit review');
      console.error('Submit review error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      setSaveStatus('saving');
      setError('');

      await reviewerService?.saveDraft({
        submissionId,
        reviewerId: user?.id,
        conferenceId: submission?.conferenceId,
        overallScore: reviewForm?.overallScore,
        detailedFeedback: reviewForm?.detailedFeedback,
        strengths: reviewForm?.strengths,
        weaknesses: reviewForm?.weaknesses,
        decision: reviewForm?.decision,
        confidenceLevel: reviewForm?.confidenceLevel,
        criteriaAnswers
      });

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save draft');
      setSaveStatus('error');
      console.error('Save draft error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectPaper = (paperId) => {
    navigate(`/streamlined-review-workspace?submissionId=${paperId}`);
    setActivePanel('review');
  };

  const handleBulkDownload = async () => {
    const downloadable = assignments?.filter(a => a?.pdfUrl);
    if (!downloadable?.length) return;

    const JSZip = (await import('jszip')).default;
    const { saveAs } = await import('file-saver');
    const zip = new JSZip();

    const conferenceNames = [...new Set(downloadable.map(a => a?.conferenceShortName).filter(Boolean))];
    const zipName = conferenceNames.length === 1 ? `${conferenceNames[0]}-papers` : 'assigned-papers';

    await Promise.all(downloadable.map(async (assignment) => {
      try {
        const ext = assignment.pdfUrl.split('.').pop() || 'pdf';
        const filename = `${assignment.paperNumber ? assignment.paperNumber + '-' : ''}${assignment.conferenceShortName || 'paper'}.${ext}`;
        const folder = conferenceNames.length > 1 ? `${assignment.conferenceShortName || 'unknown'}/` : '';
        const urlData = await submissionService?.getSubmissionFileUrl(assignment.pdfUrl, 3600, user?.id);
        const response = await fetch(urlData?.url);
        const blob = await response.blob();
        zip.file(`${folder}${filename}`, blob);
      } catch (err) {
        console.warn(`Failed to fetch file for paper ${assignment.paperNumber}:`, err);
      }
    }));

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${zipName}.zip`);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />

      <main className="page-shell">
        {/* Top Bar - Paper info + file actions */}
        {submission && !loading && !error && (
          <div className="border-b border-border bg-card px-4 lg:px-6 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-sm font-semibold text-foreground truncate">
                  {submission?.title}
                </h1>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  {submission?.paperNumber && (
                    <span className="flex items-center gap-1">
                      <Icon name="Hash" size={12} />
                      Paper #{submission?.paperNumber}
                    </span>
                  )}
                  {submission?.trackName && (
                    <span className="flex items-center gap-1">
                      <Icon name="Tag" size={12} />
                      {submission?.trackName}
                    </span>
                  )}
                  {submission?.conferenceName && (
                    <span className="flex items-center gap-1">
                      <Icon name="Building" size={12} />
                      {submission?.conferenceName}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* The paper in front of you is the one you're about to review;
                    downloading the whole queue is the occasional case. This used
                    to be the outline button while "Download All" carried the
                    primary colour, which pointed the eye at the rarer action. */}
                {fileUrl && (
                  <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                    >
                      {t('reviewWorkspace.downloadThis', 'Download this paper')}
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Tab Navigation */}
        <div className="lg:hidden border-b border-border bg-card sticky top-16 z-10">
          <div className="flex">
            <button
              onClick={() => setActivePanel('review')}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activePanel === 'review' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="Edit" size={16} className="inline mr-1.5" />
              {t('reviewWorkspace.panels.review')}
            </button>
            <button
              onClick={() => setActivePanel('paper')}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activePanel === 'paper' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground'
              }`}
            >
              <Icon name="FileText" size={16} className="inline mr-1.5" />
              {t('reviewWorkspace.panels.paper', { defaultValue: 'Paper' })}
            </button>
            <button
              onClick={() => setActivePanel('queue')}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activePanel === 'queue' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name="LayoutDashboard" size={16} className="inline mr-1.5" />
              {t('reviewWorkspace.panels.queue')} ({assignments?.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center space-y-4">
              <div className="animate-spin w-10 h-10 border-3 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="text-sm text-muted-foreground">{t('reviewWorkspace.loading')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-lg mx-auto mt-8 p-5 bg-error/10 border border-error rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-error mb-1">Error</h3>
                <p className="text-sm text-foreground/80">{error}</p>
                <button
                  onClick={() => loadWorkspaceData()}
                  className="mt-3 px-3 py-1.5 text-sm bg-error text-error-foreground font-medium hover:bg-error/90 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        ) : !submissionId ? (
          /* No paper selected - show queue only */
          <div className="max-w-2xl mx-auto p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-muted flex items-center justify-center mx-auto mb-3">
                <Icon name="FileText" size={28} className="text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold text-foreground mb-1">Select a Paper to Review</h2>
              <p className="text-sm text-muted-foreground">Choose a paper from your queue below to start reviewing.</p>
            </div>
            <ReviewerQueuePanel
              assignments={assignments}
              selectedSubmissionId={submissionId}
              onSelectPaper={handleSelectPaper}
              onBulkDownload={handleBulkDownload}
              loading={loading}
            />
          </div>
        ) : (
          <div className="lg:grid lg:grid-cols-12 lg:gap-6 p-4 lg:p-6">
            {/* Main Panel - Review Form */}
            <div className={`lg:col-span-8 space-y-4 ${
              activePanel === 'review' || activePanel === 'paper' ? 'block' : 'hidden lg:block'
            }`}>
              {/* Save Status Indicator */}
              {saveStatus && (
                <div className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${
                  saveStatus === 'saving' ? 'bg-primary/10 border border-primary text-primary' :
                  saveStatus === 'saved' ? 'bg-success/10 border border-success text-success' :
                  'bg-error/10 border border-error text-error'
                }`}>
                  <Icon
                    name={
                      saveStatus === 'saving' ? 'Loader' :
                      saveStatus === 'saved' ? 'CheckCircle' :
                      'AlertCircle'
                    }
                    size={16}
                    className={saveStatus === 'saving' ? 'animate-spin' : ''}
                  />
                  <span className="font-medium">
                    {saveStatus === 'saving' ? t('reviewWorkspace.autoSave.saving') :
                     saveStatus === 'saved' ? t('reviewWorkspace.autoSave.saved') :
                     'Save failed'}
                  </span>
                </div>
              )}

              {/* The paper itself.
                  PDFViewer existed in this folder but was never imported, so a
                  reviewer was being asked to score a paper they had no way to
                  read — only a Download button that sends them out of the app.
                  Collapsible, because once you've read it you want the form. */}
              {(fileUrl || submission?.pdfUrl) && (
                <details className="bg-card border border-border rounded-lg overflow-hidden group" open>
                  <summary className="px-4 py-3 cursor-pointer select-none flex items-center gap-2 hover:bg-muted/40 transition-colors list-none">
                    <Icon
                      name="ChevronRight"
                      size={16}
                      className="text-muted-foreground transition-transform group-open:rotate-90 rtl:rotate-180 rtl:group-open:rotate-90"
                    />
                    <Icon name="FileText" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-semibold text-foreground">
                      {t('reviewWorkspace.panels.paper', { defaultValue: 'Paper' })}
                    </span>
                    <span className="text-xs text-muted-foreground ms-auto">
                      {submission?.pdfFileName}
                    </span>
                  </summary>
                  <div className="h-[70vh] border-t border-border">
                    {/* fileUrl is the resolved/signed URL the page already fetches;
                        pdfUrl is only a storage path. */}
                    <PDFViewer pdfUrl={fileUrl || submission?.pdfUrl} submission={submission} />
                  </div>
                </details>
              )}

              {/* Review Form */}
              <ReviewForm
                reviewForm={reviewForm}
                setReviewForm={setReviewForm}
                criteriaAnswers={criteriaAnswers}
                setCriteriaAnswers={setCriteriaAnswers}
                reviewCriteria={reviewCriteria}
                submission={submission}
                onSubmit={handleSubmitReview}
                onSaveDraft={handleSaveDraft}
                saving={saving}
              />

              {/* Action Buttons (Mobile) */}
              <div className="lg:hidden space-y-3 pb-4">
                <Button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  variant="secondary"
                  className="w-full"
                  iconName="Save"
                  iconPosition="left"
                >
                  Save Draft
                </Button>
                <Button
                  onClick={handleSubmitReview}
                  disabled={saving || !canSubmit}
                  className="w-full"
                  iconName="Send"
                  iconPosition="left"
                >
                  Submit Review
                </Button>
              </div>
            </div>

            {/* Right Panel - Queue + Actions */}
            <div className={`lg:col-span-4 ${
              activePanel === 'queue' ? 'block' : 'hidden lg:block'
            }`}>
              <div className="lg:sticky lg:top-20 space-y-4">
                <ReviewerQueuePanel
                  assignments={assignments}
                  selectedSubmissionId={submissionId}
                  onSelectPaper={handleSelectPaper}
                  onBulkDownload={handleBulkDownload}
                  loading={loading}
                />

                {/* Action Buttons (Desktop) */}
                <div className="hidden lg:flex gap-3">
                  <Button
                    onClick={handleSaveDraft}
                    disabled={saving}
                    variant="secondary"
                    className="flex-1"
                    iconName="Save"
                    iconPosition="left"
                    size="sm"
                  >
                    Save Draft
                  </Button>
                  <Button
                    onClick={handleSubmitReview}
                    disabled={saving || !canSubmit}
                    className="flex-1"
                    iconName="Send"
                    iconPosition="left"
                    size="sm"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

    </div>
  );
};

export default StreamlinedReviewWorkspace;
